"use client";

import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useEditorBundle, useBundleRoot } from "@/hooks/use-editor-bundle";
import {
  CapabilityDetailLayout,
  WORKING_COPY_ID,
} from "@/components/capability-detail/CapabilityDetailLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowRightLeft,
  FileText,
  Link2,
  Loader2,
  Target,
  Zap,
} from "lucide-react";
import type {
  BindingContract,
  BindingResolution,
  BindingTarget,
} from "@/types/admin";

interface BindingTypeConfig {
  contract?: BindingContract;
  resolution?: BindingResolution;
}

function extractBindingConfig(
  data: Record<string, unknown> | undefined
): BindingTypeConfig {
  if (!data) return {};
  const wr = data.working_revision as
    | { type_config?: Record<string, unknown> }
    | undefined;
  const tc = wr?.type_config ?? (data.config as Record<string, unknown>) ?? {};
  return {
    contract: tc.contract as BindingContract | undefined,
    resolution: tc.resolution as BindingResolution | undefined,
  };
}

const strategyDescriptions: Record<string, string> = {
  single: "Deterministic — always routes to the first target unless overridden.",
  weighted:
    "Probabilistic — distributes invocations across targets by weight.",
  failover:
    "Ordered fallback — tries targets in sequence, advancing on failure.",
  conditional:
    "Expression-based — evaluates each target's condition against the input.",
};

const strategyColors: Record<string, string> = {
  single: "bg-blue-100 text-blue-700",
  weighted: "bg-purple-100 text-purple-700",
  failover: "bg-amber-100 text-amber-700",
  conditional: "bg-emerald-100 text-emerald-700",
};

function StrategyBadge({ strategy }: { strategy: string }) {
  return (
    <Badge
      variant="secondary"
      className={strategyColors[strategy] ?? "bg-slate-100 text-slate-700"}
    >
      {strategy}
    </Badge>
  );
}

function ContractSection({ contract }: { contract?: BindingContract }) {
  if (!contract) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText size={16} className="text-slate-500" />
            Contract
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 italic">
            No contract defined — this binding does not declare what abstract
            role it fulfils.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText size={16} className="text-slate-500" />
          Contract
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contract.satisfies && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
              Satisfies
            </p>
            <code className="rounded bg-slate-100 px-2 py-0.5 text-sm font-mono text-slate-700">
              {contract.satisfies}
            </code>
          </div>
        )}
        {contract.description && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
              Description
            </p>
            <p className="text-sm text-slate-700">{contract.description}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResolutionSection({
  resolution,
}: {
  resolution?: BindingResolution;
}) {
  if (!resolution) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft size={16} className="text-slate-500" />
            Resolution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 italic">
            No resolution configured.
          </p>
        </CardContent>
      </Card>
    );
  }

  const strategy = resolution.strategy ?? "single";
  const targets = resolution.targets ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ArrowRightLeft size={16} className="text-slate-500" />
          Resolution
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
              Strategy
            </p>
            <StrategyBadge strategy={strategy} />
          </div>
          <p className="text-xs text-slate-500 mt-4">
            {strategyDescriptions[strategy] ?? ""}
          </p>
        </div>

        {resolution.scope_precedence &&
          resolution.scope_precedence.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
                Scope Precedence
              </p>
              <div className="flex flex-wrap gap-1.5">
                {resolution.scope_precedence.map((scope, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {scope}
                  </Badge>
                ))}
              </div>
            </div>
          )}

        {resolution.on_no_match && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
              On No Match
            </p>
            <Badge variant="destructive" className="text-xs">
              {resolution.on_no_match}
            </Badge>
          </div>
        )}

        <TargetsTable targets={targets} strategy={strategy} />
      </CardContent>
    </Card>
  );
}

function TargetsTable({
  targets,
  strategy,
}: {
  targets: BindingTarget[];
  strategy: string;
}) {
  if (targets.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">No targets configured.</p>
    );
  }

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">
        Targets ({targets.length})
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Ref</TableHead>
              {strategy === "weighted" && (
                <TableHead className="text-xs w-20">Weight</TableHead>
              )}
              {strategy === "failover" && (
                <TableHead className="text-xs w-24">Role</TableHead>
              )}
              {strategy === "conditional" && (
                <TableHead className="text-xs">Condition</TableHead>
              )}
              <TableHead className="text-xs w-24">Timeout</TableHead>
              <TableHead className="text-xs w-20">Retries</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {targets.map((target, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-sm">
                  <div className="flex items-center gap-1.5">
                    <Target size={12} className="text-slate-400 shrink-0" />
                    {target.ref}
                  </div>
                </TableCell>
                {strategy === "weighted" && (
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {target.weight ?? 1}
                    </Badge>
                  </TableCell>
                )}
                {strategy === "failover" && (
                  <TableCell>
                    {target.role && (
                      <Badge
                        variant={
                          target.role === "primary" ? "default" : "secondary"
                        }
                        className="text-xs"
                      >
                        {target.role}
                      </Badge>
                    )}
                  </TableCell>
                )}
                {strategy === "conditional" && (
                  <TableCell>
                    {target.condition ? (
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono text-slate-600">
                        {target.condition}
                      </code>
                    ) : (
                      <span className="text-xs text-slate-400 italic">
                        default
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-xs text-slate-600">
                  {target.timeout_ms ? `${target.timeout_ms}ms` : "—"}
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {target.retries ?? 0}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export interface BindingDetailPageProps {
  appKey?: string;
  name?: string;
  headerLayerId?: string;
}

export default function BindingDetailPage({
  appKey: appKeyProp,
  name: nameProp,
  headerLayerId,
}: BindingDetailPageProps = {}) {
  const params = useParams<{ appKey: string; name: string }>();
  const appKey = appKeyProp ?? params.appKey;
  const name = nameProp ?? params.name;
  const { data: bundle, isLoading } = useEditorBundle(appKey!, name!);
  const data = useBundleRoot(bundle) as Record<string, unknown> | undefined;

  const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
  const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

  const immutableRevisions = useMemo(
    () =>
      ((data?.immutable_revisions ?? []) as Array<{
        id: string;
        type_config?: Record<string, unknown>;
      }>),
    [data]
  );

  const selectedImmutableRevision = useMemo(
    () => immutableRevisions.find((r) => r.id === selectedRevisionId),
    [immutableRevisions, selectedRevisionId]
  );

  const bindingConfig = useMemo(() => {
    if (isViewingPublished && selectedImmutableRevision) {
      const tc = selectedImmutableRevision.type_config ?? {};
      return {
        contract: tc.contract as BindingContract | undefined,
        resolution: tc.resolution as BindingResolution | undefined,
      };
    }
    return extractBindingConfig(data);
  }, [data, isViewingPublished, selectedImmutableRevision]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Binding not found.</p>
      </div>
    );
  }

  return (
    <CapabilityDetailLayout
      typeLabel="Binding"
      appKey={appKey}
      capabilityName={name}
      selectedRevisionId={selectedRevisionId}
      onRevisionChange={setSelectedRevisionId}
      headerLayerId={headerLayerId}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={18} className="text-yellow-600" />
          <h2 className="text-lg font-semibold text-slate-800">
            Binding Configuration
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ContractSection contract={bindingConfig.contract} />
          <ResolutionSection resolution={bindingConfig.resolution} />
        </div>
      </div>
    </CapabilityDetailLayout>
  );
}
